import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../lib/api';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

const NAVY = '#0076A3';

export const AdminDomainRulesTab = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [domain, setDomain] = useState('');
  const [listType, setListType] = useState('deny');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await adminAPI.getDomainRules();
      setRules(res.data.rules || []);
    } catch (err) {
      toast.error('Failed to load domain rules');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!domain.trim()) { toast.error('Enter a domain'); return; }
    setSaving(true);
    try {
      await adminAPI.addDomainRule({ domain: domain.trim(), list_type: listType, note: note.trim() });
      toast.success(`Domain ${listType === 'allow' ? 'allow' : 'deny'}-listed`);
      setDomain(''); setNote('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add rule');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, dom) => {
    if (!window.confirm(`Remove rule for ${dom}?`)) return;
    try {
      await adminAPI.deleteDomainRule(id);
      toast.success('Rule removed');
      load();
    } catch (err) { toast.error('Failed to remove'); }
  };

  const allow = rules.filter(r => r.list_type === 'allow');
  const deny = rules.filter(r => r.list_type === 'deny');
  const inputStyle = { padding: '0.625rem 0.75rem', borderRadius: 6, border: '1px solid #CBD5E1', background: '#FAF7F2', fontSize: '0.875rem', outline: 'none' };

  const RuleList = ({ title, items, color, emptyText }) => (
    <Card style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 6, padding: '1.25rem' }}>
      <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '0.9375rem', fontWeight: 700, color: NAVY, marginBottom: '0.875rem' }}>{title} ({items.length})</h3>
      {items.length === 0 ? (
        <p style={{ fontSize: '0.8125rem', color: '#94A3B8' }}>{emptyText}</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {items.map(r => (
            <div key={r._id} data-testid={`domain-rule-${r.domain}`} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.625rem 0.875rem', borderRadius: 6, border: '1px solid #EEF2F6', background: '#FAFBFC' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#19233A' }}>{r.domain}</div>
                {r.note && <div style={{ fontSize: '0.6875rem', color: '#94A3B8' }}>{r.note}</div>}
              </div>
              <span style={{ fontSize: '0.625rem', color: '#94A3B8', whiteSpace: 'nowrap' }}>{r.created_by}</span>
              <Button variant="outline" data-testid={`delete-rule-${r.domain}`} onClick={() => handleDelete(r._id, r.domain)} style={{ borderRadius: 6, fontSize: '0.6875rem', padding: '0.25rem 0.625rem', borderColor: '#FECACA', color: '#DC2626' }}>Remove</Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );

  if (loading) return <div style={{ padding: '2rem', color: '#64748B' }}>Loading domain rules...</div>;

  return (
    <div data-testid="domain-rules-tab">
      <Card style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 6, padding: '1.5rem', marginBottom: '1.25rem' }}>
        <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 700, color: NAVY, marginBottom: '0.375rem' }}>Add Domain Rule</h3>
        <p style={{ fontSize: '0.8125rem', color: '#64748B', marginBottom: '1rem' }}>
          <strong>Allow</strong>: trusted domain bypasses free/disposable/MX checks (e.g. partners). <strong>Deny</strong>: blocks registration from this domain entirely (e.g. competitors, spam).
        </p>
        <form onSubmit={handleAdd} style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <input data-testid="domain-rule-input" value={domain} onChange={e => setDomain(e.target.value)} placeholder="acme.com" style={{ ...inputStyle, flex: '1 1 200px' }} />
          <select data-testid="domain-rule-type" value={listType} onChange={e => setListType(e.target.value)} style={inputStyle}>
            <option value="deny">Deny</option>
            <option value="allow">Allow</option>
          </select>
          <input data-testid="domain-rule-note" value={note} onChange={e => setNote(e.target.value)} placeholder="Note (optional)" style={{ ...inputStyle, flex: '1 1 180px' }} />
          <Button type="submit" data-testid="domain-rule-add-btn" disabled={saving} style={{ background: NAVY, color: '#fff', borderRadius: 6, fontWeight: 600, padding: '0.625rem 1.25rem' }}>
            {saving ? 'Adding...' : 'Add Rule'}
          </Button>
        </form>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
        <RuleList title="Allow List" items={allow} color="#16A34A" emptyText="No allow-listed domains yet." />
        <RuleList title="Deny List" items={deny} color="#DC2626" emptyText="No deny-listed domains yet." />
      </div>
    </div>
  );
};
